/**
 * ============================================================================
 * 🗺️ Leaflet Tactical Multi-Domain Radar Map Component (Air & Sea)
 * ============================================================================
 */

import L from 'leaflet';
import 'leaflet.heat';
import { MAP_CONFIG } from '../utils/constants.js';
import { getAircraftIcon, getAircraftIconSvg } from '../utils/aircraftIcons.js';
import { getVesselIconSvg, getVesselColor } from '../utils/vesselIcons.js';

export class RadarMap {
  constructor(containerId = 'leaflet-map', options = {}) {
    this.containerId = containerId;
    this.options = options;
    this.map = null;
    this.tileLayer = null;
    this.currentTileKey = 'satellite_esri'; // Default to ESRI World Imagery (HD)
    
    this.markers = new Map(); // id -> L.Marker (id = "flight_REG" or "vessel_MMSI")
    this.trailLayer = null;
    this.airportLayer = null;
    this.seaportLayer = null;
    this.airwayLayer = null;
    this.shippingLaneLayer = null;
    this.firLayer = null;
    this.sectorLayer = null;
    this.waypointLayer = null;
    this.heatLayer = null;
    this.weatherLayer = null;
    this.rainRadarLayer = null;
    this.projectionLayer = null;
    this.geofenceLayer = null;
    this.rangeRingLayer = null;
    this.anchorageLayer = null;
    this.oceanLayer = null;
    this.showAirports = true;
    this.showSeaports = true;
    this.showAirways = false;
    this.showShippingLanes = false;
    this.showFIRSectors = false;
    this.showWaypoints = false;
    this.showHeatmap = false;
    this.showWeather = true;
    this.showRainRadar = false;
    this.showProjections = false;
    this.showGeofences = false;
    this.showRangeRings = false;
    this.showAnchorages = false;
    this.showOceanData = false;
    this.activeRadarCenter = 'VTBS';
    this.latestDensityPoints = [];
    this.selectedId = null;
    this.hoveredId = null;
    this.currentMode = 'all'; // 'all', 'airspace', 'marine', 'drones'
    this.latestTelemetry = { aircraft: [], vessels: [], timestamp: null };
    this.globalFlightStore = new Map(); // reg -> cumulative flight telemetry object
    this.globalVesselStore = new Map(); // mmsi -> cumulative vessel telemetry object
    this.allTargetsMap = new Map(); // id -> raw telemetry object for fast O(1) global search

    // Single Global Hover Tooltip Element
    this.hoverTooltipEl = document.getElementById('map-hover-tooltip');
    this.hoverCallsignEl = document.getElementById('hover-tooltip-callsign');
    this.hoverTelemEl = document.getElementById('hover-tooltip-telem');

    // Callbacks
    this.onSelect = options.onSelect || null;
    this.onClearSelection = options.onClearSelection || null;
    this.onSelectedPlaneChange = options.onSelectedPlaneChange || null;

    this.init();
    window.radarMap = this;
  }

  init() {
    const el = document.getElementById(this.containerId);
    if (!el) {
      console.warn(`[RadarMap] Container #${this.containerId} not found.`);
      return;
    }

    this.map = L.map(this.containerId, {
      center: MAP_CONFIG.DEFAULT_CENTER,
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      minZoom: MAP_CONFIG.MIN_ZOOM,
      maxZoom: MAP_CONFIG.MAX_ZOOM,
      zoomControl: false,
      attributionControl: false
    });

    // Zoom control in bottom right
    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // Initial Basemap: ESRI World Imagery (HD)
    this.setTileLayer('satellite_esri');

    this.trailLayer = L.layerGroup().addTo(this.map);
    this.firLayer = L.layerGroup();
    this.sectorLayer = L.layerGroup();
    this.airwayLayer = L.layerGroup();
    this.shippingLaneLayer = L.layerGroup();
    this.waypointLayer = L.layerGroup();
    this.geofenceLayer = L.layerGroup();
    this.rangeRingLayer = L.layerGroup();
    this.anchorageLayer = L.layerGroup();
    this.oceanLayer = L.layerGroup();
    this.rainRadarLayer = L.layerGroup();
    this.weatherLayer = L.layerGroup().addTo(this.map);
    this.projectionLayer = L.layerGroup();
    this.airportLayer = L.layerGroup().addTo(this.map);
    this.seaportLayer = L.layerGroup().addTo(this.map);
    this.measureLayer = L.layerGroup().addTo(this.map);

    // Measurement Tool State
    this.isMeasuring = false;
    this.measureStartLatLng = null;
    this.measureTempLine = null;
    this.measureTooltip = null;

    // Click on map background -> Handle measurement or clear selection
    this.map.on('click', (e) => {
      if (this.isMeasuring) {
        this.handleMeasureClick(e.latlng);
        return;
      }
      this.clearSelection();
      this.hideHoverTooltip();
    });

    this.map.on('mousemove', (e) => {
      if (this.isMeasuring && this.measureStartLatLng) {
        this.handleMeasureMouseMove(e.latlng);
      }
    });

    const syncPositions = () => {
      if (this.hoveredId) {
        const marker = this.markers.get(this.hoveredId);
        if (marker && this.isZoomDetailed()) {
          const pt = this.map.latLngToContainerPoint(marker.getLatLng());
          this.positionHoverTooltip(pt);
        } else {
          this.hideHoverTooltip();
        }
      }
    };

    this.map.on('move zoom zoomanim viewreset resize moveend zoomend', syncPositions);

    el.addEventListener('mouseleave', () => {
      this.hideHoverTooltip();
    });

    // 🚀 Start Real-Time 60 FPS Dead Reckoning Kinematics Loop
    this.initAnimationLoop();
  }

  initAnimationLoop() {
    this._animFrameId = null;
    this._lastAnimTime = performance.now();

    const animate = (now) => {
      const dt = Math.min(Math.max((now - this._lastAnimTime) / 1000, 0), 0.1);
      this._lastAnimTime = now;

      if (dt > 0.001) {
        this.updateDeadReckoning(dt);
      }
      this._animFrameId = requestAnimationFrame(animate);
    };

    this._animFrameId = requestAnimationFrame(animate);

    // 🛡️ Zoom Animation Synchronization Guard & Dynamic Viewport Culling
    if (this.map) {
      this.map.on('moveend', () => {
        this.renderMultiDomain();
      });

      this.map.on('zoomend', () => {
        this.renderMultiDomain();
        for (const [id, marker] of this.markers.entries()) {
          if (!id.startsWith('flight_')) continue;
          const ms = marker.motionState;
          if (ms) {
            marker.setLatLng([ms.currentLat, ms.currentLng]);
            if (id === this.selectedId) {
              this.appendTrailPoint(ms.currentLat, ms.currentLng, id);
            }
          }
        }
      });
    }
  }

  updateDeadReckoning(dt) {
    if (!this.map) return;
    const now = Date.now();

    for (const [id, marker] of this.markers.entries()) {
      // 🛡️ User Choice: Only aircraft move continuously (Marine AIS stays at reported points)
      if (!id.startsWith('flight_')) continue;
      const ms = marker.motionState;
      if (!ms) continue;

      // 🛡️ Signal timeout guard: if no update for > 35s, stop dead reckoning drift
      if (now - ms.lastSnapshotTime > 35000) continue;

      // 🛡️ Ground / Stationary guard: do not drift parked or stationary aircraft
      if (ms.isOnGround || ms.speedKts < 15) {
        if (ms.lastRawLat != null && ms.lastRawLng != null) {
          ms.currentLat = ms.lastRawLat;
          ms.currentLng = ms.lastRawLng;
          marker.setLatLng([ms.currentLat, ms.currentLng]);
        }
        continue;
      }

      // 1. Smooth Heading Rotation (shortest angular turn, max 30 deg/sec)
      let diffDeg = (ms.targetHeadingDeg - ms.headingDeg + 540) % 360 - 180;
      const maxTurn = 30 * dt;
      if (Math.abs(diffDeg) <= maxTurn) {
        ms.headingDeg = ms.targetHeadingDeg;
      } else {
        ms.headingDeg = (ms.headingDeg + Math.sign(diffDeg) * maxTurn + 360) % 360;
      }

      // 2. Continuous Uninterrupted Physical Forward Motion
      // Ground speed in m/s (1 knot = 0.5144444 m/s) - Invariant Cruising Speed!
      const baseSpeedMps = ms.speedKts * 0.5144444;

      // 3. Along-Track Catch-Up (Positive only, never backward!)
      // Limit catch-up adjustment to max +10% of base speed to avoid abrupt surges
      let catchupMps = 0;
      if (ms.offsetAlongMeters > 0) {
        const desiredCatchup = ms.offsetAlongMeters * 0.5; // gentle bleed over 2 seconds
        catchupMps = Math.min(desiredCatchup, baseSpeedMps * 0.10);
        ms.offsetAlongMeters = Math.max(0, ms.offsetAlongMeters - catchupMps * dt);
      } else {
        ms.offsetAlongMeters = 0;
      }

      const forwardDistMeters = (baseSpeedMps + catchupMps) * dt;

      // 4. Cross-Track Asymptotic Alignment (Lateral corridor drift)
      let crossShiftMeters = 0;
      if (Math.abs(ms.offsetCrossMeters || 0) > 0.001) {
        const blendRate = 1 - Math.exp(-1.2 * dt);
        crossShiftMeters = ms.offsetCrossMeters * blendRate;
        ms.offsetCrossMeters -= crossShiftMeters;
      }

      // 5. Decompose Net Motion to Geographical Lat/Lng
      const rad = (ms.headingDeg * Math.PI) / 180;
      const cosH = Math.cos(rad);
      const sinH = Math.sin(rad);

      // Along-track is +forward along heading; Cross-track is +right perpendicular to heading
      const dy = forwardDistMeters * cosH - crossShiftMeters * sinH;
      const dx = forwardDistMeters * sinH + crossShiftMeters * cosH;

      const dLat = dy / 111139;
      const cosLat = Math.cos((ms.currentLat * Math.PI) / 180);
      const metersPerLng = 111139 * (Math.abs(cosLat) > 0.0001 ? cosLat : 1);
      const dLng = dx / metersPerLng;

      ms.currentLat += dLat;
      ms.currentLng += dLng;

      // 6. Update Leaflet Marker Position (Pause DOM writes during CSS zoom animation to prevent visual dislocation)
      if (!this.map._animatingZoom) {
        marker.setLatLng([ms.currentLat, ms.currentLng]);
      }

      // 7. Rotate Plane Icon Smoothly
      const roundedHdg = Math.round(ms.headingDeg);
      if (marker.currentHeading !== roundedHdg) {
        marker.currentHeading = roundedHdg;
        const el = marker.getElement();
        if (el) {
          const iconEl = el.querySelector('.plane-icon');
          if (iconEl) iconEl.style.transform = `rotate(${roundedHdg}deg)`;
        }
      }

      // 8. Smooth Live Trail for Selected Aircraft
      if (id === this.selectedId && !this.map._animatingZoom) {
        this.appendTrailPoint(ms.currentLat, ms.currentLng, id);
      }
    }
  }

  setTileLayer(key) {
    const tileConfig = MAP_CONFIG.TILES[key];
    if (!tileConfig) return;

    if (this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
    }

    const options = {
      maxZoom: tileConfig.maxZoom || 19,
      attribution: tileConfig.attribution
    };
    if (tileConfig.subdomains) {
      options.subdomains = tileConfig.subdomains;
    }

    this.tileLayer = L.tileLayer(tileConfig.url, options).addTo(this.map);
    this.currentTileKey = key;
  }

  isZoomDetailed() {
    return this.map ? this.map.getZoom() >= 6 : false;
  }

  setMode(mode) {
    this.currentMode = mode;
    this.renderMultiDomain();
  }

  createPlaneIcon(plane, isSelected = false) {
    const heading = parseInt(plane.track_deg) || 0;
    const isEmergency = plane.is_emergency || ['7700', '7600', '7500'].includes(String(plane.squawk || ''));
    const isDrone = Boolean(plane.is_drone || plane.category === 'drone');

    let stateClass = '';
    if (isSelected) stateClass = 'selected';
    else if (isEmergency) stateClass = 'emergency';
    else if (isDrone) stateClass = 'drone-target';
    if (plane.is_on_ground) stateClass += ' on-ground';

    const iconSvg = getAircraftIconSvg(plane);

    const html = `
      <div class="plane-marker-wrapper ${stateClass}">
        ${isSelected ? '<div class="pulse-ring"></div><div class="pulse-ring d2"></div>' : ''}
        ${isDrone && !isSelected ? '<div class="pulse-ring drone-pulse"></div>' : ''}
        <div class="plane-icon" style="transform: rotate(${heading}deg);">${iconSvg}</div>
      </div>
    `;

    return L.divIcon({
      className: 'custom-plane-marker',
      html: html,
      iconSize: [48, 48],
      iconAnchor: [24, 24]
    });
  }

  createVesselIcon(vessel, isSelected = false) {
    const heading = parseInt(vessel.true_heading || vessel.cog_deg || 0);
    const cat = vessel.ship_category || 'general';
    const color = getVesselColor(cat);
    const iconSvg = getVesselIconSvg(cat);

    const html = `
      <div class="vessel-marker ${isSelected ? 'selected' : ''}">
        ${isSelected ? '<div class="pulse-ring"></div>' : ''}
        <div class="vessel-icon-wrapper" style="transform: rotate(${heading}deg); color: ${color};">
          ${iconSvg}
        </div>
      </div>
    `;

    return L.divIcon({
      className: 'custom-vessel-marker',
      html: html,
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
  }

  showHoverTooltip(item, screenPt, isVessel = false) {
    const id = isVessel ? `vessel_${item.mmsi}` : `flight_${item.registration}`;
    if (!this.hoverTooltipEl || !this.isZoomDetailed() || id === this.selectedId) {
      this.hideHoverTooltip();
      return;
    }

    this.hoveredId = id;

    if (isVessel) {
      const name = item.name || `MMSI: ${item.mmsi}`;
      const sog = item.sog_kts != null ? `${item.sog_kts} kts` : '0 kts';
      const type = item.ship_type_thai || 'เรือ';
      if (this.hoverCallsignEl) this.hoverCallsignEl.textContent = `🚢 ${name}`;
      if (this.hoverTelemEl) this.hoverTelemEl.textContent = `SPD ${sog} \u00B7 ${type}`;
    } else {
      const callsign = item.callsign || item.registration || 'N/A';
      const altText = item.altitude_ft || '0 ft';
      const spdText = item.ground_speed_kts || '0 kts';
      if (this.hoverCallsignEl) this.hoverCallsignEl.textContent = `✈️ ${callsign}`;
      if (this.hoverTelemEl) this.hoverTelemEl.textContent = `ALT ${altText} \u00B7 SPD ${spdText}`;
    }

    this.positionHoverTooltip(screenPt);
    this.hoverTooltipEl.classList.remove('hidden');
  }

  positionHoverTooltip(screenPt) {
    if (!this.hoverTooltipEl || !screenPt) return;

    let left = screenPt.x + 18;
    let top = screenPt.y - 25;

    if (left + 180 > window.innerWidth) {
      left = screenPt.x - 190;
    }
    if (top < 10) {
      top = screenPt.y + 20;
    }

    this.hoverTooltipEl.style.left = `${left}px`;
    this.hoverTooltipEl.style.top = `${top}px`;
  }

  hideHoverTooltip(id = null) {
    if (id && this.hoveredId !== id) return;
    this.hoveredId = null;
    if (this.hoverTooltipEl) {
      this.hoverTooltipEl.classList.add('hidden');
    }
  }

  isCoordInBounds(lat, lng, bounds) {
    if (lat == null || lng == null || !bounds) return false;
    const s = bounds.getSouth();
    const n = bounds.getNorth();
    const latMin = Math.min(s, n);
    const latMax = Math.max(s, n);
    if (lat < latMin || lat > latMax) return false;

    const w = bounds.getWest();
    const e = bounds.getEast();
    const span = Math.abs(e - w);
    if (span >= 350) return true; // Full world view

    const normLng = ((lng % 360) + 360) % 360;
    const normW = ((w % 360) + 360) % 360;
    const normE = ((e % 360) + 360) % 360;

    if (normW <= normE) {
      return normLng >= normW && normLng <= normE;
    } else {
      return normLng >= normW || normLng <= normE;
    }
  }

  updateMultiDomain({ aircraft = [], vessels = [], timestamp = null }) {
    if (!this.map) return;
    const now = Date.now();
    this.latestTelemetry = { aircraft, vessels, timestamp };

    // 1. Merge incoming live telemetry into persistent spatial store
    aircraft.forEach(p => {
      const reg = (p.registration || p.hex_id || p.callsign || '').trim().toUpperCase();
      if (reg && p.latitude != null && p.longitude != null) {
        p._lastSeen = now;
        this.globalFlightStore.set(reg, p);
        this.allTargetsMap.set(`flight_${reg}`, p);
        this.allTargetsMap.set(reg, p);
      }
    });

    vessels.forEach(v => {
      const mmsi = String(v.mmsi || '').trim();
      if (mmsi && v.latitude != null && v.longitude != null) {
        v._lastSeen = now;
        this.globalVesselStore.set(mmsi, v);
        this.allTargetsMap.set(`vessel_${mmsi}`, v);
        this.allTargetsMap.set(mmsi, v);
      }
    });

    // 2. Prune targets that haven't been seen in > 50 seconds (dropped off radar)
    for (const [reg, p] of this.globalFlightStore.entries()) {
      if (now - (p._lastSeen || 0) > 50000 && `flight_${reg}` !== this.selectedId) {
        this.globalFlightStore.delete(reg);
        this.allTargetsMap.delete(`flight_${reg}`);
        this.allTargetsMap.delete(reg);
      }
    }

    for (const [mmsi, v] of this.globalVesselStore.entries()) {
      if (now - (v._lastSeen || 0) > 60000 && `vessel_${mmsi}` !== this.selectedId) {
        this.globalVesselStore.delete(mmsi);
        this.allTargetsMap.delete(`vessel_${mmsi}`);
        this.allTargetsMap.delete(mmsi);
      }
    }

    this.renderMultiDomain();
  }

  /**
   * 🛰️ Flightradar24-Grade Spatial Grid Thinning & Dynamic LOD Filter
   * Guarantees total rendered DOM markers on screen stays around 250-350 at all zoom levels!
   */
  getVisibleAircraftList(aircraft = [], zoom, bounds) {
    if (this.currentMode === 'marine') return [];

    const forceRender = [];
    const candidates = [];

    for (let i = 0; i < aircraft.length; i++) {
      const plane = aircraft[i];
      if (this.currentMode === 'drones' && !plane.is_drone) continue;
      const reg = plane.registration;
      if (!reg || plane.latitude == null || plane.longitude == null) continue;

      const id = `flight_${reg}`;
      const isSelected = (id === this.selectedId);
      const isEmergency = plane.is_emergency || ['7700', '7600', '7500'].includes(String(plane.squawk || ''));
      const isDrone = Boolean(plane.is_drone || plane.category === 'drone');

      // Selected flight, emergencies, or tactical reconnaissance drones: ALWAYS render
      if (isSelected || ((isEmergency || isDrone) && this.isCoordInBounds(plane.latitude, plane.longitude, bounds))) {
        forceRender.push(plane);
        continue;
      }

      // Viewport culling for all others
      if (!this.isCoordInBounds(plane.latitude, plane.longitude, bounds)) continue;

      const altFt = typeof plane.altitude_ft === 'number'
        ? plane.altitude_ft
        : (parseInt(String(plane.altitude_ft || plane.altitude_raw || 0).replace(/[^0-9-]/g, '')) || 0);

      // Altitude pre-filtering by zoom level
      if (zoom <= 3 && altFt < 26000) continue;
      if (zoom <= 5 && altFt < 16000) continue;
      if (zoom <= 7 && altFt < 6000) continue;
      if (zoom <= 9 && altFt < 1500) continue;

      candidates.push({ plane, altFt });
    }

    // If total visible candidates on screen is already <= 300 (e.g. Thailand, SE Asia, regional views), render all!
    if (candidates.length <= 300 || zoom >= 8) {
      return [...forceRender, ...candidates.map(c => c.plane)];
    }

    // Flightradar24 Spatial Grid Cell Thinning (Active only during macro worldwide views with thousands of flights)
    const cellLat = zoom <= 1 ? 6.0 : zoom === 2 ? 4.5 : zoom === 3 ? 3.0 : zoom === 4 ? 1.8 : zoom === 5 ? 1.0 : zoom === 6 ? 0.5 : 0.25;
    const cellLng = cellLat * 1.33;
    const maxPerCell = zoom <= 3 ? 1 : 2;

    const cells = new Map();

    for (let i = 0; i < candidates.length; i++) {
      const { plane, altFt } = candidates[i];
      const cellKey = `${Math.floor(plane.latitude / cellLat)}_${Math.floor(plane.longitude / cellLng)}`;
      const speed = typeof plane.ground_speed_kts === 'number' ? plane.ground_speed_kts : (parseFloat(plane.ground_speed_kts || 0) || 0);
      const isWidebody = ['A388', 'B77W', 'B744', 'B789', 'A359', 'A35K', 'B772', 'A333'].includes(plane.aircraft_code) ? 5000 : 0;
      const score = (altFt * 0.7) + (speed * 0.3) + isWidebody;

      const existing = cells.get(cellKey);
      if (!existing) {
        cells.set(cellKey, [{ plane, score }]);
      } else if (existing.length < maxPerCell) {
        existing.push({ plane, score });
        existing.sort((a, b) => b.score - a.score);
      } else if (score > existing[existing.length - 1].score) {
        existing[existing.length - 1] = { plane, score };
        existing.sort((a, b) => b.score - a.score);
      }
    }

    const thinnedList = [...forceRender];
    for (const list of cells.values()) {
      for (let j = 0; j < list.length; j++) {
        thinnedList.push(list[j].plane);
      }
    }

    return thinnedList;
  }

  getVisibleVesselList(vessels = [], zoom, bounds) {
    if (this.currentMode === 'airspace' || this.currentMode === 'drones') return [];

    const result = [];
    for (let i = 0; i < vessels.length; i++) {
      const vessel = vessels[i];
      const mmsi = vessel.mmsi;
      if (!mmsi || vessel.latitude == null || vessel.longitude == null) continue;

      const id = `vessel_${mmsi}`;
      const isSelected = (id === this.selectedId);

      if (!isSelected && !this.isCoordInBounds(vessel.latitude, vessel.longitude, bounds)) continue;

      const shipCat = (vessel.ship_category || 'general').toLowerCase();
      if (!isSelected && zoom <= 4) {
        if (!['cargo', 'tanker', 'passenger', 'military'].includes(shipCat)) continue;
      }

      result.push(vessel);
      if (zoom <= 4 && result.length >= 100) break;
    }
    return result;
  }

  renderMultiDomain() {
    if (!this.map) return;
    const aircraft = Array.from(this.globalFlightStore.values());
    const vessels = Array.from(this.globalVesselStore.values());
    const activeIds = new Set();
    const zoom = this.map.getZoom();
    // 🛡️ Viewport Culling with 20% Screen Buffer for zero visual pop-in
    const bounds = this.map.getBounds().pad(0.20);

    const visiblePlanes = this.getVisibleAircraftList(aircraft, zoom, bounds);
    const visibleVessels = this.getVisibleVesselList(vessels, zoom, bounds);

    // 1. Process Aircraft with Flightradar24-Grade LOD & Viewport Culling
    visiblePlanes.forEach(plane => {
      const reg = plane.registration;
      const id = `flight_${reg}`;
      const isSelected = (id === this.selectedId);
      const realLat = plane.latitude;
      const realLng = plane.longitude;

      activeIds.add(id);
      plane.domain = 'airspace';
      if (!plane.category || plane.category === 'commercial') {
        plane.category = plane.is_drone ? 'drone' : 'aircraft';
      }

      const heading = parseInt(plane.track_deg) || 0;
      const rawSpd = typeof plane.ground_speed_kts === 'number'
        ? plane.ground_speed_kts
        : parseFloat(plane.ground_speed_kts || plane.speed_kts || 0) || 0;
      const isOnGround = Boolean(plane.is_on_ground) || rawSpd < 15;
      const isEmergency = plane.is_emergency || ['7700', '7600', '7500'].includes(String(plane.squawk || ''));

      if (this.markers.has(id)) {
        const marker = this.markers.get(id);
        marker.targetData = plane;

        if (!marker.motionState) {
          marker.motionState = {
            currentLat: realLat,
            currentLng: realLng,
            lastRawLat: realLat,
            lastRawLng: realLng,
            speedKts: rawSpd,
            headingDeg: heading,
            targetHeadingDeg: heading,
            offsetAlongMeters: 0,
            offsetCrossMeters: 0,
            lastSnapshotTime: Date.now(),
            isOnGround
          };
          marker.setLatLng([realLat, realLng]);
        } else {
          const ms = marker.motionState;
          const isSameCoord = (ms.lastRawLat === realLat && ms.lastRawLng === realLng);

          if (!isSameCoord) {
            ms.lastRawLat = realLat;
            ms.lastRawLng = realLng;

            const dist = L.latLng(ms.currentLat, ms.currentLng).distanceTo([realLat, realLng]);

            if (dist > 25000) {
              ms.currentLat = realLat;
              ms.currentLng = realLng;
              ms.offsetAlongMeters = 0;
              ms.offsetCrossMeters = 0;
              marker.setLatLng([realLat, realLng]);
            } else if (!isOnGround && rawSpd >= 15) {
              const dy = (realLat - ms.currentLat) * 111139;
              const cosLat = Math.cos((ms.currentLat * Math.PI) / 180);
              const dx = (realLng - ms.currentLng) * 111139 * (Math.abs(cosLat) > 0.0001 ? cosLat : 1);

              const rad = (heading * Math.PI) / 180;
              const cosH = Math.cos(rad);
              const sinH = Math.sin(rad);

              const errAlong = dy * cosH + dx * sinH;
              const errCross = -dy * sinH + dx * cosH;

              if (errAlong > 0) {
                ms.offsetAlongMeters = Math.min(errAlong, 600);
              } else {
                ms.offsetAlongMeters = 0;
              }

              ms.offsetCrossMeters = Math.max(-400, Math.min(errCross, 400));
            } else {
              ms.currentLat = realLat;
              ms.currentLng = realLng;
              ms.offsetAlongMeters = 0;
              ms.offsetCrossMeters = 0;
            }
          }

          ms.speedKts = rawSpd;
          ms.targetHeadingDeg = heading;
          ms.lastSnapshotTime = Date.now();
          ms.isOnGround = isOnGround;
        }

        if (
          marker.currentSelected !== isSelected ||
          marker.currentAircraftCode !== plane.aircraft_code ||
          marker.currentEmergency !== isEmergency ||
          marker.currentOnGround !== isOnGround
        ) {
          marker.setIcon(this.createPlaneIcon(plane, isSelected));
          marker.currentSelected = isSelected;
          marker.currentAircraftCode = plane.aircraft_code;
          marker.currentEmergency = isEmergency;
          marker.currentOnGround = isOnGround;
          marker.currentHeading = heading;
        }
      } else {
        const marker = L.marker([realLat, realLng], {
          icon: this.createPlaneIcon(plane, isSelected)
        });
        marker.targetData = plane;
        marker.currentSelected = isSelected;
        marker.currentHeading = heading;
        marker.currentAircraftCode = plane.aircraft_code;
        marker.currentEmergency = isEmergency;
        marker.currentOnGround = isOnGround;

        marker.motionState = {
          currentLat: realLat,
          currentLng: realLng,
          lastRawLat: realLat,
          lastRawLng: realLng,
          speedKts: rawSpd,
          headingDeg: heading,
          targetHeadingDeg: heading,
          offsetAlongMeters: 0,
          offsetCrossMeters: 0,
          lastSnapshotTime: Date.now(),
          isOnGround
        };

        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          this.hideHoverTooltip();
          this.select(marker.targetData || plane, id);
        });

        marker.on('mouseover', () => {
          if (id !== this.selectedId) {
            const pt = this.map.latLngToContainerPoint(marker.getLatLng());
            this.showHoverTooltip(marker.targetData, pt, false);
          }
        });

        marker.on('mouseout', () => {
          this.hideHoverTooltip(id);
        });

        marker.addTo(this.map);
        this.markers.set(id, marker);
      }
    });

    // 2. Process Marine Vessels with Viewport Culling & LOD
    visibleVessels.forEach(vessel => {
      const mmsi = vessel.mmsi;
      const id = `vessel_${mmsi}`;
      const isSelected = (id === this.selectedId);
      const latLng = [vessel.latitude, vessel.longitude];

      activeIds.add(id);
      vessel.category = 'vessel';

      if (this.markers.has(id)) {
        const marker = this.markers.get(id);
        marker.setLatLng(latLng);
        marker.targetData = vessel;

        const heading = parseInt(vessel.true_heading || vessel.cog_deg || 0);
        if (marker.currentSelected !== isSelected || marker.currentShipCat !== vessel.ship_category) {
          marker.setIcon(this.createVesselIcon(vessel, isSelected));
          marker.currentSelected = isSelected;
          marker.currentShipCat = vessel.ship_category;
          marker.currentHeading = heading;
        } else if (marker.currentHeading !== heading) {
          marker.currentHeading = heading;
          const el = marker.getElement();
          if (el) {
            const iconEl = el.querySelector('.vessel-icon-wrapper');
            if (iconEl) iconEl.style.transform = `rotate(${heading}deg)`;
          }
        }
      } else {
        const marker = L.marker(latLng, {
          icon: this.createVesselIcon(vessel, isSelected)
        });
        marker.targetData = vessel;
        marker.currentSelected = isSelected;
        marker.currentHeading = parseInt(vessel.true_heading || vessel.cog_deg || 0);
        marker.currentShipCat = vessel.ship_category;

        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          this.hideHoverTooltip();
          this.select(marker.targetData || vessel, id);
        });

        marker.on('mouseover', () => {
          if (id !== this.selectedId) {
            const pt = this.map.latLngToContainerPoint(marker.getLatLng());
            this.showHoverTooltip(marker.targetData, pt, true);
          }
        });

        marker.on('mouseout', () => {
          this.hideHoverTooltip(id);
        });

        marker.addTo(this.map);
        this.markers.set(id, marker);
      }
    });

    // 3. Purge Culled / Off-Screen Markers from Map DOM
    for (const [id, marker] of this.markers.entries()) {
      if (!activeIds.has(id)) {
        if (this.hoveredId === id) this.hideHoverTooltip();
        this.map.removeLayer(marker);
        this.markers.delete(id);
      }
    }

    if (this.selectedId && this.onSelectedPlaneChange) {
      const marker = this.markers.get(this.selectedId);
      const selData = marker?.targetData || this.allTargetsMap.get(this.selectedId);
      if (selData) this.onSelectedPlaneChange(selData);
    }
  }

  // Compatibility method
  update(aircraftList = []) {
    this.updateMultiDomain({ aircraft: aircraftList, vessels: [] });
  }

  select(item, customId = null) {
    const isVessel = (item.category === 'vessel' || !item.registration);
    const id = customId || (isVessel ? `vessel_${item.mmsi}` : `flight_${item.registration}`);
    
    // 🛡️ CRITICAL FIX: Always clear previous trail immediately when selecting target
    this.clearTrail();
    this.selectedId = id;
    this.trailOwnerId = id;
    this.hideHoverTooltip();

    // Prefer latest marker targetData if available
    const marker = this.markers.get(id);
    const currentData = marker?.targetData || item;

    for (const [mId, m] of this.markers.entries()) {
      const isSel = (mId === this.selectedId);
      if (m.currentSelected !== isSel) {
        if (mId.startsWith('vessel_')) {
          m.setIcon(this.createVesselIcon(m.targetData, isSel));
        } else {
          m.setIcon(this.createPlaneIcon(m.targetData, isSel));
        }
        m.currentSelected = isSel;
      }
    }

    // 🚀 If target payload already carries its own breadcrumbs trail, render it immediately!
    const trail = currentData.trail || item.trail;
    if (trail && Array.isArray(trail) && trail.length >= 2) {
      const trailPoints = trail.map(p => ({
        lat: p[0],
        lon: p[1],
        alt_ft: p[2],
        ts: p[3]
      }));
      this.drawTrail(trailPoints);
    }

    if (this.onSelect) {
      this.onSelect(currentData);
    }
  }

  clearSelection() {
    this.selectedId = null;
    this.trailOwnerId = null;
    this.hideHoverTooltip();

    for (const [mId, marker] of this.markers.entries()) {
      if (marker.currentSelected) {
        if (mId.startsWith('vessel_')) {
          marker.setIcon(this.createVesselIcon(marker.targetData, false));
        } else {
          marker.setIcon(this.createPlaneIcon(marker.targetData, false));
        }
        marker.currentSelected = false;
      }
    }
    this.clearTrail();
    if (this.onClearSelection) this.onClearSelection();
  }

  appendTrailPoint(lat, lon, ownerId = null) {
    if (lat == null || lon == null) return;
    // 🛡️ Guard: Only append if this point belongs to the currently selected aircraft
    if (ownerId && this.selectedId && ownerId !== this.selectedId) return;

    if (this.trailOwnerId !== this.selectedId) {
      this.clearTrail();
      this.trailOwnerId = this.selectedId;
    }

    const latLng = L.latLng(lat, lon);
    if (!this.currentTrailPolyline) {
      if (this._singlePointBuffer) {
        const distMeters = this._singlePointBuffer.distanceTo(latLng);
        if (distMeters > 10 && distMeters < 15000) {
          this.currentTrailPolyline = L.polyline([this._singlePointBuffer, latLng], {
            color: '#38bdf8',
            weight: 2.5,
            opacity: 0.9
          }).addTo(this.trailLayer);
          this._singlePointBuffer = null;
        }
      } else {
        this._singlePointBuffer = latLng;
      }
      return;
    }

    const pts = this.currentTrailPolyline.getLatLngs();
    if (pts.length > 0) {
      const last = pts[pts.length - 1];
      const distMeters = last.distanceTo(latLng);

      // 🛡️ TELEPORT JUMP FILTER:
      // In a 2-5s poll, an aircraft moving at 600 kts (~300 m/s) travels at most ~1.5 km.
      // If jump > 15,000 meters (15 km), it is a DIFFERENT aircraft or bad GPS glitch.
      // Do NOT draw a cross-country line! Reset trail for the new coordinate.
      if (distMeters > 15000) {
        this.clearTrail();
        this._singlePointBuffer = latLng;
        return;
      }

      if (distMeters > 10) {
        if (pts.length >= 1000) {
          pts.shift();
          this.currentTrailPolyline.setLatLngs(pts);
        }
        this.currentTrailPolyline.addLatLng(latLng);
      }
    } else {
      this.currentTrailPolyline.addLatLng(latLng);
    }
  }

  drawTrail(points = []) {
    this.trailLayer.clearLayers();
    this.currentTrailPolyline = null;
    this._singlePointBuffer = null;
    if (!points || points.length < 2) return;

    const GAP_THRESHOLD_SECONDS = 300;
    const segments = [[points[0]]];
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const cur = points[i];

      // Distance calculation in km
      const dLat = (cur.lat - prev.lat) * 111;
      const dLon = (cur.lon - prev.lon) * 111 * Math.cos((prev.lat * Math.PI) / 180);
      const distKm = Math.hypot(dLat, dLon);

      // 🛡️ Split line if time gap is too large OR physical jump > 35 km
      if ((cur.ts && prev.ts && (cur.ts - prev.ts) > GAP_THRESHOLD_SECONDS) || distKm > 35) {
        segments.push([]);
      }
      segments[segments.length - 1].push(cur);
    }

    const recentSegment = segments[segments.length - 1];
    if (recentSegment.length < 2) return;

    const latLngs = recentSegment.map(p => [p.lat, p.lon]);

    // 🚀 Bridge historical trail seamlessly to current aircraft position!
    if (this.selectedId && this.markers.has(this.selectedId)) {
      const marker = this.markers.get(this.selectedId);
      if (marker && marker.motionState) {
        const lastPt = L.latLng(latLngs[latLngs.length - 1]);
        const curPt = L.latLng(marker.motionState.currentLat, marker.motionState.currentLng);
        if (lastPt.distanceTo(curPt) < 25000) {
          latLngs.push([curPt.lat, curPt.lng]);
        }
      }
    }

    this.currentTrailPolyline = L.polyline(latLngs, {
      color: '#38bdf8',
      weight: 2.5,
      opacity: 0.9
    }).addTo(this.trailLayer);
  }

  clearTrail() {
    this.trailLayer.clearLayers();
    this.currentTrailPolyline = null;
    this._singlePointBuffer = null;
  }

  // --------------------------------------------------------------------------
  // 🧭 Infrastructure Layer Rendering (Airports & Seaports)
  // --------------------------------------------------------------------------
  createAirportMarker(airport) {
    const isIntl = airport.type === 'international';
    const html = `
      <div class="infra-marker infra-airport ${airport.type}">
        <div class="infra-pin">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
          </svg>
        </div>
        <span class="infra-label">${airport.iata || airport.icao}</span>
      </div>
    `;
    const icon = L.divIcon({
      className: 'custom-infra-marker',
      html: html,
      iconSize: [36, 44],
      iconAnchor: [18, 22],
      popupAnchor: [0, -22]
    });

    const marker = L.marker([airport.latitude, airport.longitude], { icon });

    const rwyText = (airport.runways || []).map(r => `${r.designator} (${r.length_m}m)`).join(', ') || 'N/A';
    const freqs = airport.frequencies ? Object.entries(airport.frequencies).map(([k, v]) => `${k} ${v}`).join(' · ') : '-';

    const popupHtml = `
      <div class="tactical-popup-body">
        <div class="tactical-popup-head">
          <span class="tactical-popup-title">🛫 ${airport.iata} / ${airport.icao}</span>
          <span class="tactical-popup-tag air">${isIntl ? 'INTL HUB' : 'DOMESTIC'}</span>
        </div>
        <div class="tactical-popup-sub">${airport.name_th}<br/><small style="color: #94a3b8;">${airport.name_en}</small></div>
        <div class="tactical-popup-grid">
          <span class="label">City / Region</span><span class="val">${airport.city}</span>
          <span class="label">Elevation</span><span class="val">${airport.elevation_ft} ft</span>
          <span class="label">Runways</span><span class="val">${rwyText}</span>
          <span class="label">Radio Freq</span><span class="val">${freqs}</span>
        </div>
      </div>
    `;

    marker.bindPopup(popupHtml, {
      className: 'tactical-popup',
      maxWidth: 320
    });

    return marker;
  }

  createSeaportMarker(seaport) {
    const html = `
      <div class="infra-marker infra-seaport ${seaport.type}">
        <div class="infra-pin">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M12 2a3 3 0 0 0-3 3c0 .35.06.69.17 1H6v2h1.08C7.5 12.3 9.4 16 11 16.9V21h-3v2h8v-2h-3v-4.1c1.6-.9 3.5-4.6 3.92-8.9H18V6h-3.17c.11-.31.17-.65.17-1a3 3 0 0 0-3-3zm0 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
        </div>
        <span class="infra-label">${(seaport.unlocode || seaport.id).replace('TH ', '')}</span>
      </div>
    `;
    const icon = L.divIcon({
      className: 'custom-infra-marker',
      html: html,
      iconSize: [36, 44],
      iconAnchor: [18, 22],
      popupAnchor: [0, -22]
    });

    const marker = L.marker([seaport.latitude, seaport.longitude], { icon });

    const cargoText = (seaport.cargo_types || []).slice(0, 3).join(', ') || '-';
    const vhfText = seaport.vhf_channels ? Object.entries(seaport.vhf_channels).map(([k, v]) => `${k}: ${v}`).join(' · ') : '-';

    const popupHtml = `
      <div class="tactical-popup-body">
        <div class="tactical-popup-head">
          <span class="tactical-popup-title">⚓ ${seaport.unlocode}</span>
          <span class="tactical-popup-tag sea">${seaport.type.toUpperCase().replace(/_/g, ' ')}</span>
        </div>
        <div class="tactical-popup-sub">${seaport.name_th}<br/><small style="color: #94a3b8;">${seaport.name_en}</small></div>
        <div class="tactical-popup-grid">
          <span class="label">Waterbody</span><span class="val">${seaport.waterbody}</span>
          <span class="label">Max Draft</span><span class="val">${seaport.max_draft_m} m</span>
          <span class="label">Terminals</span><span class="val">${seaport.terminals_count} Berths</span>
          <span class="label">Cargo</span><span class="val">${cargoText}</span>
          <span class="label">VHF Comms</span><span class="val">${vhfText}</span>
        </div>
      </div>
    `;

    marker.bindPopup(popupHtml, {
      className: 'tactical-popup',
      maxWidth: 340
    });

    return marker;
  }

  setAirports(airports = []) {
    if (!this.airportLayer) return;
    this.airportLayer.clearLayers();
    airports.forEach(airport => {
      if (airport.latitude != null && airport.longitude != null) {
        const marker = this.createAirportMarker(airport);
        marker.addTo(this.airportLayer);
      }
    });
  }

  setSeaports(seaports = []) {
    if (!this.seaportLayer) return;
    this.seaportLayer.clearLayers();
    seaports.forEach(seaport => {
      if (seaport.latitude != null && seaport.longitude != null) {
        const marker = this.createSeaportMarker(seaport);
        marker.addTo(this.seaportLayer);
      }
    });
  }

  toggleAirports(visible = null) {
    if (visible === null) {
      this.showAirports = !this.showAirports;
    } else {
      this.showAirports = !!visible;
    }

    if (this.showAirports) {
      if (!this.map.hasLayer(this.airportLayer)) this.map.addLayer(this.airportLayer);
    } else {
      if (this.map.hasLayer(this.airportLayer)) this.map.removeLayer(this.airportLayer);
    }
    return this.showAirports;
  }

  toggleSeaports(visible = null) {
    if (visible === null) {
      this.showSeaports = !this.showSeaports;
    } else {
      this.showSeaports = !!visible;
    }

    if (this.showSeaports) {
      if (!this.map.hasLayer(this.seaportLayer)) this.map.addLayer(this.seaportLayer);
    } else {
      if (this.map.hasLayer(this.seaportLayer)) this.map.removeLayer(this.seaportLayer);
    }
    return this.showSeaports;
  }

  // --------------------------------------------------------------------------
  // 🛣️ Flight Corridors & Shipping Lanes Rendering
  // --------------------------------------------------------------------------
  setAirways(airways = []) {
    if (!this.airwayLayer) return;
    this.airwayLayer.clearLayers();

    airways.forEach(aw => {
      if (!aw.coordinates || aw.coordinates.length < 2) return;

      const polyline = L.polyline(aw.coordinates, {
        color: aw.color || '#38bdf8',
        weight: 2.5,
        dashArray: '6, 8',
        opacity: 0.75,
        lineCap: 'round',
        lineJoin: 'round'
      });

      const popupHtml = `
        <div class="tactical-popup-body">
          <div class="tactical-popup-head">
            <span class="tactical-popup-title">🛣️ ${aw.code}</span>
            <span class="tactical-popup-tag air">AIRWAY</span>
          </div>
          <div class="tactical-popup-sub">${aw.name}</div>
          <div class="tactical-popup-grid">
            <span class="label">Level Range</span><span class="val">${aw.level_range}</span>
            <span class="label">Direction</span><span class="val">${aw.direction}</span>
            <span class="label">Waypoints</span><span class="val">${(aw.waypoints || []).join(' &rarr; ')}</span>
          </div>
        </div>
      `;

      polyline.bindPopup(popupHtml, {
        className: 'tactical-popup',
        maxWidth: 340
      });

      polyline.on('mouseover', () => {
        polyline.setStyle({ weight: 4.5, opacity: 1.0 });
      });
      polyline.on('mouseout', () => {
        polyline.setStyle({ weight: 2.5, opacity: 0.75 });
      });

      polyline.addTo(this.airwayLayer);
    });
  }

  setShippingLanes(lanes = []) {
    if (!this.shippingLaneLayer) return;
    this.shippingLaneLayer.clearLayers();

    lanes.forEach(lane => {
      if (!lane.coordinates || lane.coordinates.length < 2) return;

      const polyline = L.polyline(lane.coordinates, {
        color: lane.color || '#10b981',
        weight: 3.0,
        dashArray: '10, 10',
        opacity: 0.8,
        lineCap: 'round',
        lineJoin: 'round'
      });

      const popupHtml = `
        <div class="tactical-popup-body">
          <div class="tactical-popup-head">
            <span class="tactical-popup-title">🚢 ${lane.code}</span>
            <span class="tactical-popup-tag sea">SHIPPING LANE</span>
          </div>
          <div class="tactical-popup-sub">${lane.name}</div>
          <div class="tactical-popup-grid">
            <span class="label">Waterbody</span><span class="val">${lane.waterbody}</span>
            <span class="label">Corridor Width</span><span class="val">${lane.width_nm} NM</span>
            <span class="label">Traffic Type</span><span class="val">${lane.traffic_type}</span>
          </div>
        </div>
      `;

      polyline.bindPopup(popupHtml, {
        className: 'tactical-popup',
        maxWidth: 340
      });

      polyline.on('mouseover', () => {
        polyline.setStyle({ weight: 5.0, opacity: 1.0 });
      });
      polyline.on('mouseout', () => {
        polyline.setStyle({ weight: 3.0, opacity: 0.8 });
      });

      polyline.addTo(this.shippingLaneLayer);
    });
  }

  toggleAirways(visible = null) {
    if (visible === null) {
      this.showAirways = !this.showAirways;
    } else {
      this.showAirways = !!visible;
    }

    if (this.showAirways) {
      if (!this.map.hasLayer(this.airwayLayer)) this.map.addLayer(this.airwayLayer);
    } else {
      if (this.map.hasLayer(this.airwayLayer)) this.map.removeLayer(this.airwayLayer);
    }
    return this.showAirways;
  }

  toggleShippingLanes(visible = null) {
    if (visible === null) {
      this.showShippingLanes = !this.showShippingLanes;
    } else {
      this.showShippingLanes = !!visible;
    }

    if (this.showShippingLanes) {
      if (!this.map.hasLayer(this.shippingLaneLayer)) this.map.addLayer(this.shippingLaneLayer);
    } else {
      if (this.map.hasLayer(this.shippingLaneLayer)) this.map.removeLayer(this.shippingLaneLayer);
    }
    return this.showShippingLanes;
  }

  focusTarget(lat, lon, targetId = null) {
    if (lat == null || lon == null || !this.map) return;
    this.map.flyTo([lat, lon], 12, { animate: true, duration: 1.5 });
    if (targetId) {
      const cleanId = String(targetId).trim().toUpperCase();
      const rawTarget = this.allTargetsMap.get(`flight_${cleanId}`) ||
                        this.allTargetsMap.get(`vessel_${cleanId}`) ||
                        this.allTargetsMap.get(cleanId);
      if (rawTarget) {
        this.select(rawTarget);
      } else {
        const flightMarker = this.markers.get(`flight_${cleanId}`);
        const vesselMarker = this.markers.get(`vessel_${cleanId}`);
        const targetMarker = flightMarker || vesselMarker;
        if (targetMarker && targetMarker.targetData) {
          this.select(targetMarker.targetData);
        }
      }
    }
  }

  renderAirspace(data) {
    if (!data) return;

    // 1. Render Bangkok FIR Polygon
    if (data.fir && data.fir.coordinates) {
      this.firLayer.clearLayers();
      const fir = data.fir;
      const polygon = L.polygon(fir.coordinates, {
        color: fir.color || '#38bdf8',
        weight: 2.5,
        dashArray: '8, 8',
        fillColor: '#0284c7',
        fillOpacity: 0.03,
        className: 'fir-boundary-polygon'
      });

      polygon.bindPopup(`
        <div class="tactical-popup">
          <div class="popup-title" style="color: ${fir.color}">📡 ${fir.name}</div>
          <div class="popup-subtitle">ICAO: ${fir.icao} &bull; ${fir.country}</div>
          <div class="popup-grid">
            <div><span class="lbl">ACC:</span> ${fir.center}</div>
            <div><span class="lbl">Limits:</span> ${fir.altitude_limits}</div>
          </div>
        </div>
      `, { className: 'tactical-leaflet-popup' });

      polygon.addTo(this.firLayer);
    }

    // 2. Render TMA Sectors
    if (data.sectors && Array.isArray(data.sectors)) {
      this.sectorLayer.clearLayers();
      data.sectors.forEach(sector => {
        const poly = L.polygon(sector.coordinates, {
          color: sector.color || '#0ea5e9',
          weight: 1.8,
          dashArray: '4, 4',
          fillColor: sector.color || '#0ea5e9',
          fillOpacity: 0.06,
          className: 'tma-sector-polygon'
        });

        const freqs = (sector.frequencies || []).join(', ');
        poly.bindPopup(`
          <div class="tactical-popup">
            <div class="popup-title" style="color: ${sector.color}">📡 ${sector.name}</div>
            <div class="popup-subtitle">CODE: ${sector.code}</div>
            <div class="popup-grid">
              <div><span class="lbl">Agency:</span> ${sector.agency}</div>
              <div><span class="lbl">Limits:</span> ${sector.altitude_limits}</div>
              <div style="grid-column: span 2;"><span class="lbl">VHF:</span> ${freqs}</div>
            </div>
          </div>
        `, { className: 'tactical-leaflet-popup' });

        poly.addTo(this.sectorLayer);
      });
    }

    // 3. Render Enroute Waypoints (Fixes)
    if (data.waypoints && Array.isArray(data.waypoints)) {
      this.waypointLayer.clearLayers();
      data.waypoints.forEach(wp => {
        const iconHtml = `
          <div class="waypoint-marker">
            <div class="waypoint-diamond"></div>
            <div class="waypoint-label">${wp.name}</div>
          </div>
        `;
        const customIcon = L.divIcon({
          html: iconHtml,
          className: 'custom-waypoint-icon',
          iconSize: [60, 24],
          iconAnchor: [30, 12]
        });

        const marker = L.marker([wp.latitude, wp.longitude], { icon: customIcon });
        const awStr = (wp.airways || []).join(', ');
        marker.bindPopup(`
          <div class="tactical-popup">
            <div class="popup-title" style="color: #a855f7;">📍 WAYPOINT: ${wp.name}</div>
            <div class="popup-subtitle">${wp.type}</div>
            <div class="popup-grid">
              <div><span class="lbl">Lat/Lon:</span> ${wp.latitude.toFixed(4)}, ${wp.longitude.toFixed(4)}</div>
              <div><span class="lbl">Airways:</span> ${awStr || 'DIRECT'}</div>
            </div>
          </div>
        `, { className: 'tactical-leaflet-popup' });

        marker.addTo(this.waypointLayer);
      });
    }
  }

  toggleFIRSectors(visible = null) {
    if (visible === null) {
      this.showFIRSectors = !this.showFIRSectors;
    } else {
      this.showFIRSectors = !!visible;
    }

    if (this.showFIRSectors) {
      if (!this.map.hasLayer(this.firLayer)) this.map.addLayer(this.firLayer);
      if (!this.map.hasLayer(this.sectorLayer)) this.map.addLayer(this.sectorLayer);
    } else {
      if (this.map.hasLayer(this.firLayer)) this.map.removeLayer(this.firLayer);
      if (this.map.hasLayer(this.sectorLayer)) this.map.removeLayer(this.sectorLayer);
    }
    return this.showFIRSectors;
  }

  toggleWaypoints(visible = null) {
    if (visible === null) {
      this.showWaypoints = !this.showWaypoints;
    } else {
      this.showWaypoints = !!visible;
    }

    if (this.showWaypoints) {
      if (!this.map.hasLayer(this.waypointLayer)) this.map.addLayer(this.waypointLayer);
    } else {
      if (this.map.hasLayer(this.waypointLayer)) this.map.removeLayer(this.waypointLayer);
    }
    return this.showWaypoints;
  }

  updateDensityHeatmap(data) {
    if (!data || !data.points) return;
    
    let pts = [];
    if (this.currentMode === 'airspace') {
      pts = data.points.air || [];
    } else if (this.currentMode === 'marine') {
      pts = data.points.marine || [];
    } else {
      pts = data.points.all || [];
    }

    this.latestDensityPoints = pts;

    if (this.heatLayer) {
      this.heatLayer.setLatLngs(pts);
    } else if (this.showHeatmap) {
      this.initHeatLayer();
    }
  }

  initHeatLayer() {
    if (this.heatLayer) {
      this.map.removeLayer(this.heatLayer);
    }

    this.heatLayer = L.heatLayer(this.latestDensityPoints, {
      radius: 28,
      blur: 18,
      maxZoom: 14,
      max: 1.0,
      minOpacity: 0.35,
      gradient: {
        0.2: '#38bdf8',
        0.4: '#10b981',
        0.6: '#eab308',
        0.8: '#f97316',
        1.0: '#ef4444'
      }
    });

    if (this.showHeatmap && this.map) {
      this.heatLayer.addTo(this.map);
    }
  }

  toggleHeatmap(visible = null) {
    if (visible === null) {
      this.showHeatmap = !this.showHeatmap;
    } else {
      this.showHeatmap = !!visible;
    }

    if (this.showHeatmap) {
      if (!this.heatLayer) {
        this.initHeatLayer();
      } else if (!this.map.hasLayer(this.heatLayer)) {
        this.map.addLayer(this.heatLayer);
      }
    } else {
      if (this.heatLayer && this.map.hasLayer(this.heatLayer)) {
        this.map.removeLayer(this.heatLayer);
      }
    }
    return this.showHeatmap;
  }

  renderWeather(data) {
    if (!data) return;
    this.weatherLayer.clearLayers();

    // Render 100% Real-time METAR Weather Stations
    const stations = data.stations || (Array.isArray(data) ? data : []);
    if (Array.isArray(stations)) {
      stations.forEach(st => {
        if (!st.latitude || !st.longitude) return;

        const catColor = st.category === 'VFR' ? '#10b981' : (st.category === 'MVFR' ? '#38bdf8' : (st.category === 'IFR' ? '#f59e0b' : '#ef4444'));
        const iconHtml = `
          <div class="weather-station-marker">
            <div class="weather-pin-badge">
              <span class="weather-icon">${st.icon || '⛅'}</span>
              <span class="weather-temp">${st.temp_c != null ? st.temp_c : '--'}&deg;C</span>
            </div>
            <div class="weather-station-code">${st.icao}</div>
          </div>
        `;

        const customIcon = L.divIcon({
          html: iconHtml,
          className: 'custom-weather-station-icon',
          iconSize: [68, 32],
          iconAnchor: [34, 16]
        });

        const marker = L.marker([st.latitude, st.longitude], { icon: customIcon });
        const gustTxt = st.wind_gust_kts > 0 ? ` (Gust ${st.wind_gust_kts} kts)` : '';
        const dewTxt = st.dewpoint_c != null ? `${st.dewpoint_c}°C` : '--';
        const humTxt = st.humidity_pct != null ? `${st.humidity_pct}%` : '--';
        const qnhTxt = st.qnh_hpa != null ? `${st.qnh_hpa} hPa` : '--';
        const visTxt = st.visibility || '10+ km';
        const rawMetar = st.raw_metar || `METAR ${st.icao}`;

        marker.bindPopup(`
          <div class="tactical-popup">
            <div class="popup-title" style="color: #38bdf8;">🌤️ ${st.name}</div>
            <div class="popup-subtitle">ICAO: ${st.icao} &bull; CATEGORY: <strong style="color: ${catColor};">${st.category || 'VFR'}</strong></div>
            <div class="popup-grid">
              <div><span class="lbl">Condition:</span> <b>${st.condition || 'Clear'}</b></div>
              <div><span class="lbl">Temp / Dew:</span> ${st.temp_c}°C / ${dewTxt}</div>
              <div><span class="lbl">Humidity:</span> ${humTxt}</div>
              <div><span class="lbl">Wind Vector:</span> ${st.wind_deg}° @ ${st.wind_speed_kts || 0} kts${gustTxt}</div>
              <div><span class="lbl">Visibility:</span> ${visTxt}</div>
              <div><span class="lbl">Altimeter (QNH):</span> ${qnhTxt}</div>
              <div><span class="lbl">Cloud Cover:</span> ${st.cloud_cover || 'Clear'}</div>
              <div><span class="lbl">Report Time:</span> ${st.report_time || 'Real-time'}</div>
            </div>
            <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); font-family: monospace; font-size: 10px; color: #94a3b8; word-break: break-all;">
              <code>${rawMetar}</code>
            </div>
          </div>
        `, { className: 'tactical-leaflet-popup' });

        marker.addTo(this.weatherLayer);
      });
    }

    // Auto-update rain radar if config provided
    if (data.radar) {
      this.renderRainRadar(data.radar);
    }
  }

  renderRainRadar(radarConfig) {
    if (!radarConfig || !this.rainRadarLayer) return;
    this.rainRadarLayer.clearLayers();

    if (radarConfig.tile_url_template) {
      const tileUrl = radarConfig.tile_url_template;
      const rainTileLayer = L.tileLayer(tileUrl, {
        opacity: 0.65,
        zIndex: 250,
        maxZoom: 18,
        tileSize: 256
      });
      this.rainRadarLayer.addLayer(rainTileLayer);
    }
  }

  toggleWeather(visible = null) {
    if (visible === null) {
      this.showWeather = !this.showWeather;
    } else {
      this.showWeather = !!visible;
    }

    if (this.showWeather) {
      if (!this.map.hasLayer(this.weatherLayer)) this.map.addLayer(this.weatherLayer);
    } else {
      if (this.map.hasLayer(this.weatherLayer)) this.map.removeLayer(this.weatherLayer);
    }
    return this.showWeather;
  }

  toggleRainRadar(visible = null) {
    if (visible === null) {
      this.showRainRadar = !this.showRainRadar;
    } else {
      this.showRainRadar = !!visible;
    }

    if (this.showRainRadar) {
      if (!this.map.hasLayer(this.rainRadarLayer)) this.map.addLayer(this.rainRadarLayer);
    } else {
      if (this.map.hasLayer(this.rainRadarLayer)) this.map.removeLayer(this.rainRadarLayer);
    }
    return this.showRainRadar;
  }

  updateProjections(data) {
    if (!this.projectionLayer) return;
    this.projectionLayer.clearLayers();
    if (!data || !this.showProjections) return;

    const allProjections = [
      ...(data.air_projections || []),
      ...(data.marine_projections || [])
    ];

    allProjections.forEach(proj => {
      const isAir = proj.domain === 'airspace';
      const color = isAir ? '#38bdf8' : '#10b981';

      // 1. Dashed Velocity Line
      const poly = L.polyline(proj.polyline, {
        color: color,
        weight: 2,
        dashArray: '4, 6',
        opacity: 0.75,
        className: 'velocity-vector-line'
      });

      poly.bindPopup(`
        <div class="tactical-popup">
          <div class="popup-title" style="color: ${color};">📈 Velocity Vector Leader</div>
          <div class="popup-subtitle">${proj.callsign} (${proj.target_id})</div>
          <div class="popup-grid">
            <div><span class="lbl">Speed:</span> ${proj.speed_kts} kts</div>
            <div><span class="lbl">Heading:</span> ${proj.heading_deg}&deg;</div>
            <div><span class="lbl">5m Extent:</span> ${proj.waypoints[0].distance_nm} NM</div>
            <div><span class="lbl">15m Extent:</span> ${proj.waypoints[2].distance_nm} NM</div>
          </div>
        </div>
      `, { className: 'tactical-leaflet-popup' });

      poly.addTo(this.projectionLayer);

      // 2. 15-Minute Target Endpoint Dot
      if (proj.waypoints && proj.waypoints.length === 3) {
        const p15 = proj.waypoints[2];
        const dot = L.circleMarker([p15.latitude, p15.longitude], {
          radius: 3,
          color: color,
          fillColor: color,
          fillOpacity: 0.9,
          weight: 1
        });
        dot.addTo(this.projectionLayer);
      }
    });
  }

  toggleProjections(visible = null) {
    if (visible === null) {
      this.showProjections = !this.showProjections;
    } else {
      this.showProjections = !!visible;
    }

    if (this.showProjections) {
      if (!this.map.hasLayer(this.projectionLayer)) this.map.addLayer(this.projectionLayer);
    } else {
      if (this.map.hasLayer(this.projectionLayer)) this.map.removeLayer(this.projectionLayer);
    }
    return this.showProjections;
  }

  renderGeofences(data) {
    if (!data) return;
    this.geofenceLayer.clearLayers();

    const zones = data.zones || data;
    if (!Array.isArray(zones)) return;

    zones.forEach(z => {
      const poly = L.polygon(z.coordinates, {
        color: z.color || '#ef4444',
        weight: 1.5,
        dashArray: '6, 6',
        fillColor: z.color || '#ef4444',
        fillOpacity: 0.18,
        className: 'geofence-polygon'
      });

      const occupantsCount = z.total_occupants ?? 0;
      const statusBadge = occupantsCount > 0
        ? `<strong style="color: #ef4444;">⚠️ ACTIVE CONTACTS: ${occupantsCount}</strong>`
        : `<strong style="color: #10b981;">✅ SECURE (0 Contacts)</strong>`;

      poly.bindPopup(`
        <div class="tactical-popup">
          <div class="popup-title" style="color: ${z.color};">⚡ ${z.id}: ${z.name}</div>
          <div class="popup-subtitle">TYPE: ${z.type} &bull; ${statusBadge}</div>
          <div class="popup-grid">
            <div><span class="lbl">Authority:</span> ${z.authority}</div>
            <div><span class="lbl">Vertical Limits:</span> ${z.floor} - ${z.ceiling}</div>
            <div><span class="lbl">Schedule:</span> ${z.active_time}</div>
            <div><span class="lbl">Surveillance:</span> Continuous Geo-Fence Radar</div>
          </div>
        </div>
      `, { className: 'tactical-leaflet-popup' });

      poly.addTo(this.geofenceLayer);
    });
  }

  toggleGeofences(visible = null) {
    if (visible === null) {
      this.showGeofences = !this.showGeofences;
    } else {
      this.showGeofences = !!visible;
    }

    if (this.showGeofences) {
      if (!this.map.hasLayer(this.geofenceLayer)) this.map.addLayer(this.geofenceLayer);
    } else {
      if (this.map.hasLayer(this.geofenceLayer)) this.map.removeLayer(this.geofenceLayer);
    }
    return this.showGeofences;
  }

  renderRangeRings(config) {
    if (!config || !config.center) return;
    this.rangeRingLayer.clearLayers();

    const center = config.center;
    const centerLat = center.latitude;
    const centerLon = center.longitude;

    // 1. Center Radar Hub Marker
    const hubIcon = L.divIcon({
      html: `
        <div class="radar-center-hub-marker">
          <div class="radar-hub-pulse"></div>
          <div class="radar-hub-dot"></div>
          <div class="radar-hub-label">${center.id}</div>
        </div>
      `,
      className: 'custom-radar-hub-icon',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    const hubMarker = L.marker([centerLat, centerLon], { icon: hubIcon });
    hubMarker.bindPopup(`
      <div class="tactical-popup">
        <div class="popup-title" style="color: #38bdf8;">🎯 ${center.name}</div>
        <div class="popup-subtitle">RADAR ANCHOR: ${center.id}</div>
        <div class="popup-grid">
          <div><span class="lbl">Role:</span> ${center.description}</div>
          <div><span class="lbl">Position:</span> ${centerLat.toFixed(4)}&deg;N, ${centerLon.toFixed(4)}&deg;E</div>
          <div><span class="lbl">Coverage:</span> Concentric 25-200 NM Rings</div>
        </div>
      </div>
    `, { className: 'tactical-leaflet-popup' });
    hubMarker.addTo(this.rangeRingLayer);

    // 2. Concentric Distance Rings
    if (config.rings && Array.isArray(config.rings)) {
      config.rings.forEach(r => {
        const circle = L.circle([centerLat, centerLon], {
          radius: r.radius_meters,
          color: r.color || '#38bdf8',
          weight: 1.2,
          dashArray: '4, 4',
          fill: false,
          opacity: 0.6,
          className: 'tactical-range-ring'
        });

        circle.bindPopup(`
          <div class="tactical-popup">
            <div class="popup-title" style="color: ${r.color};">🎯 Range Ring: ${r.label}</div>
            <div class="popup-subtitle">RADIUS: ${r.nm} NM &bull; ${r.km} KM</div>
            <div class="popup-grid">
              <div><span class="lbl">Center:</span> ${center.name} (${center.id})</div>
              <div><span class="lbl">Radius Meters:</span> ${r.radius_meters.toLocaleString()} m</div>
            </div>
          </div>
        `, { className: 'tactical-leaflet-popup' });

        circle.addTo(this.rangeRingLayer);
      });
    }

    // 3. Cardinal Bearing Radial Crosshairs (000°, 090°, 180°, 270°)
    const maxRadiusMeters = 200 * 1852;
    const bearings = [0, 90, 180, 270];
    const earthRadius = 6371000;

    bearings.forEach(deg => {
      const rad = deg * (Math.PI / 180);
      const latRad = centerLat * (Math.PI / 180);
      const lonRad = centerLon * (Math.PI / 180);
      const distRatio = maxRadiusMeters / earthRadius;

      const endLatRad = Math.asin(
        Math.sin(latRad) * Math.cos(distRatio) +
        Math.cos(latRad) * Math.sin(distRatio) * Math.cos(rad)
      );
      const endLonRad = lonRad + Math.atan2(
        Math.sin(rad) * Math.sin(distRatio) * Math.cos(latRad),
        Math.cos(distRatio) - Math.sin(latRad) * Math.sin(endLatRad)
      );

      const endLat = endLatRad * (180 / Math.PI);
      const endLon = ((endLonRad * (180 / Math.PI)) + 540) % 360 - 180;

      const line = L.polyline([[centerLat, centerLon], [endLat, endLon]], {
        color: 'rgba(56, 189, 248, 0.35)',
        weight: 1,
        dashArray: '2, 6'
      });
      line.addTo(this.rangeRingLayer);
    });
  }

  toggleRangeRings(visible = null) {
    if (visible === null) {
      this.showRangeRings = !this.showRangeRings;
    } else {
      this.showRangeRings = !!visible;
    }

    if (this.showRangeRings) {
      if (!this.map.hasLayer(this.rangeRingLayer)) this.map.addLayer(this.rangeRingLayer);
    } else {
      if (this.map.hasLayer(this.rangeRingLayer)) this.map.removeLayer(this.rangeRingLayer);
    }
    return this.showRangeRings;
  }

  renderAnchorages(data) {
    if (!data || !data.zones || !Array.isArray(data.zones)) return;
    this.anchorageLayer.clearLayers();

    data.zones.forEach(z => {
      if (!z.coordinates || z.coordinates.length < 3) return;

      // 1. Anchorage Area Polygon
      const poly = L.polygon(z.coordinates, {
        color: z.color || '#38bdf8',
        weight: 1.5,
        dashArray: '5, 5',
        fillColor: z.color || '#38bdf8',
        fillOpacity: 0.12,
        className: 'anchorage-polygon'
      });

      const occupantsList = z.occupants && z.occupants.length > 0
        ? z.occupants.map(o => `&bull; ${o.name} (${o.sog_kts} kts - ${o.status_tag})`).join('<br>')
        : '<span style="color:#64748b;">No vessels currently inside</span>';

      const popupHtml = `
        <div class="tactical-popup">
          <div class="popup-title" style="color: ${z.color};">⚓ ${z.id}: ${z.name}</div>
          <div class="popup-subtitle">${z.thai_name} &bull; ${z.type}</div>
          <div class="popup-grid">
            <div><span class="lbl">Port / Area:</span> ${z.port}</div>
            <div><span class="lbl">VHF Radio:</span> ${z.vhf_channel}</div>
            <div><span class="lbl">Depth:</span> ${z.depth_meters}</div>
            <div><span class="lbl">Seabed:</span> ${z.seabed_type}</div>
            <div><span class="lbl">Purpose:</span> ${z.purpose}</div>
            <div><span class="lbl">Authority:</span> ${z.authority}</div>
            <div style="grid-column: 1 / -1; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
              <span class="lbl">Occupants (${z.total_occupants || 0}):</span><br>
              <div style="font-size: 11px; margin-top: 2px;">${occupantsList}</div>
            </div>
          </div>
        </div>
      `;

      poly.bindPopup(popupHtml, { className: 'tactical-leaflet-popup' });
      poly.addTo(this.anchorageLayer);

      // 2. Anchor Icon Marker at Center
      if (z.center && z.center.length === 2) {
        const anchorIcon = L.divIcon({
          html: `
            <div class="anchorage-center-marker" style="border-color: ${z.color}; color: ${z.color};">
              <span class="anch-icon">⚓</span>
              <span class="anch-badge">${z.total_occupants || 0}</span>
            </div>
          `,
          className: 'custom-anchorage-icon',
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        });

        const marker = L.marker([z.center[0], z.center[1]], { icon: anchorIcon });
        marker.bindPopup(popupHtml, { className: 'tactical-leaflet-popup' });
        marker.addTo(this.anchorageLayer);
      }
    });
  }

  toggleAnchorages(visible = null) {
    if (visible === null) {
      this.showAnchorages = !this.showAnchorages;
    } else {
      this.showAnchorages = !!visible;
    }

    if (this.showAnchorages) {
      if (!this.map.hasLayer(this.anchorageLayer)) this.map.addLayer(this.anchorageLayer);
    } else {
      if (this.map.hasLayer(this.anchorageLayer)) this.map.removeLayer(this.anchorageLayer);
    }
    return this.showAnchorages;
  }

  // ==========================================================================
  // 🎯 Tactical Range & Bearing Measurement Tool
  // ==========================================================================
  toggleMeasureMode(active = null) {
    if (active === null) {
      this.isMeasuring = !this.isMeasuring;
    } else {
      this.isMeasuring = !!active;
    }

    const container = document.getElementById(this.containerId);
    if (this.isMeasuring) {
      if (container) container.classList.add('measuring-cursor');
    } else {
      if (container) container.classList.remove('measuring-cursor');
      this.clearMeasurement();
    }
    return this.isMeasuring;
  }

  clearMeasurement() {
    this.measureLayer.clearLayers();
    this.measureStartLatLng = null;
    this.measureTempLine = null;
    this.measureTooltip = null;
  }

  calcGeodesicLocal(lat1, lon1, lat2, lon2) {
    const R = 6371.0; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distKm = R * c;
    const distNm = distKm * 0.539957;

    const y = Math.sin(dLon) * Math.cos(lat2 * Math.PI / 180);
    const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
      Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLon);
    const bearing = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;

    return {
      distanceNm: distNm.toFixed(2),
      distanceKm: distKm.toFixed(2),
      bearingDeg: bearing.toFixed(1)
    };
  }

  handleMeasureClick(latlng) {
    if (!this.measureStartLatLng) {
      // Step 1: Set Start Point
      this.clearMeasurement();
      this.measureStartLatLng = latlng;

      const startIcon = L.divIcon({
        html: `<div class="measure-point-pin start-pin"><span>START</span></div>`,
        className: 'custom-measure-icon',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });

      L.marker(latlng, { icon: startIcon }).addTo(this.measureLayer);

      this.measureTempLine = L.polyline([latlng, latlng], {
        color: '#eab308',
        weight: 2,
        dashArray: '6, 6',
        opacity: 0.9,
        className: 'measure-active-line'
      }).addTo(this.measureLayer);
    } else {
      // Step 2: Set End Point & Finalize
      const start = this.measureStartLatLng;
      const end = latlng;
      const calc = this.calcGeodesicLocal(start.lat, start.lng, end.lat, end.lng);

      const endIcon = L.divIcon({
        html: `<div class="measure-point-pin end-pin"><span>END</span></div>`,
        className: 'custom-measure-icon',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });

      L.marker(end, { icon: endIcon }).addTo(this.measureLayer);

      // Solid final line
      L.polyline([start, end], {
        color: '#22c55e',
        weight: 3,
        opacity: 0.9,
        className: 'measure-solid-line'
      }).addTo(this.measureLayer);

      // Mid-point badge popup
      const midLat = (start.lat + end.lat) / 2;
      const midLng = (start.lng + end.lng) / 2;
      const badgeIcon = L.divIcon({
        html: `
          <div class="measure-badge-popup">
            <div class="measure-val">📏 ${calc.distanceNm} NM (${calc.distanceKm} KM)</div>
            <div class="measure-sub">🧭 True Bearing: ${calc.bearingDeg}°</div>
          </div>
        `,
        className: 'custom-measure-badge',
        iconSize: [160, 40],
        iconAnchor: [80, 20]
      });

      L.marker([midLat, midLng], { icon: badgeIcon }).addTo(this.measureLayer);

      // Reset for next measurement
      this.measureStartLatLng = null;
      if (this.measureTempLine) {
        this.measureLayer.removeLayer(this.measureTempLine);
        this.measureTempLine = null;
      }
    }
  }

  handleMeasureMouseMove(latlng) {
    if (this.measureTempLine && this.measureStartLatLng) {
      this.measureTempLine.setLatLngs([this.measureStartLatLng, latlng]);
    }
  }



  // ==========================================================================
  // 🌊 Real-Time Oceanographic Data & Sea State Observations (Open-Meteo Marine)
  // ==========================================================================
  renderOceanData(oceanZones) {
    if (!this.oceanLayer) return;
    this.oceanLayer.clearLayers();

    const zones = Array.isArray(oceanZones) ? oceanZones : (oceanZones?.ocean_zones || []);
    if (!Array.isArray(zones)) return;

    zones.forEach((z) => {
      if (typeof z.latitude !== 'number' || typeof z.longitude !== 'number') return;

      const color = z.severity_color || '#06b6d4';
      const waveHeight = z.wave_height_m != null ? z.wave_height_m : 0.5;

      const buoyHtml = `
        <div class="ocean-buoy-marker" style="border-color: ${color}; box-shadow: 0 0 8px ${color}66;">
          <span class="ocean-icon">🌊</span>
          <span class="ocean-wave-badge" style="background: ${color};">${waveHeight}m</span>
        </div>
      `;

      const customBuoyIcon = L.divIcon({
        html: buoyHtml,
        className: 'custom-ocean-buoy-icon',
        iconSize: [36, 36],
        iconAnchor: [18, 18]
      });

      const buoyMarker = L.marker([z.latitude, z.longitude], { icon: customBuoyIcon });
      const popupHtml = `
        <div class="tactical-popup ocean-popup">
          <div class="popup-header" style="border-left: 3px solid ${color};">
            <div class="popup-title">🌊 ${z.name || z.id}</div>
            <div class="popup-subtitle">${z.name_th || ''} &bull; ${z.region || 'Thailand Waters'}</div>
          </div>
          <div class="popup-grid">
            <div><span class="lbl">Sea State:</span> <span class="badge" style="background: ${color}; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: 700; font-size: 11px;">${z.sea_state || 'Moderate'}</span></div>
            <div><span class="lbl">Wave Height (Hs):</span> <b style="color: #38bdf8; font-size: 13px;">${waveHeight} Meters</b></div>
            <div><span class="lbl">Wave Period:</span> ${z.wave_period_s || '--'} Seconds</div>
            <div><span class="lbl">Wave Direction:</span> ${z.wave_direction_deg || 0}°</div>
            <div><span class="lbl">Surface Current:</span> <b style="color: #34d399;">${z.current_speed_kts || 0} kts (${z.current_velocity_kmh || 0} km/h)</b></div>
            <div><span class="lbl">Current Heading:</span> ${z.current_direction_deg || 0}°</div>
            <div><span class="lbl">Depth Category:</span> ${z.depth_category || 'Continental Shelf'}</div>
            <div><span class="lbl">Obs Time:</span> ${z.observation_time || 'Live Real-time'}</div>
          </div>
        </div>
      `;
      buoyMarker.bindPopup(popupHtml, { className: 'tactical-leaflet-popup' });
      buoyMarker.addTo(this.oceanLayer);
    });
  }

  toggleOceanData(visible = null) {
    if (visible === null) {
      this.showOceanData = !this.showOceanData;
    } else {
      this.showOceanData = !!visible;
    }

    if (this.showOceanData) {
      if (!this.map.hasLayer(this.oceanLayer)) this.map.addLayer(this.oceanLayer);
    } else {
      if (this.map.hasLayer(this.oceanLayer)) this.map.removeLayer(this.oceanLayer);
    }
    return this.showOceanData;
  }

}


